import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TSButton from 'components/common/Button/Button';
import {
  useGetOperatorWelfareCheckSettingsQuery,
  useUpdateOperatorWelfareCheckSettingsMutation,
  useTriggerWelfareCheckPollMutation,
} from 'app/api/welfareCheckAdminApiSlice';

// Each Operator owns its Welfare Check settings; an Admin edits those of the
// Operators they're Admin for (ADR-0011).
const WelfareCheckSettings = () => {
  const { data: operators = [], isLoading } =
    useGetOperatorWelfareCheckSettingsQuery();

  const [operatorId, setOperatorId] = useState('');
  const [gracePeriodMinutes, setGracePeriodMinutes] = useState('');
  const [reminderIntervalMinutes, setReminderIntervalMinutes] = useState('');
  const [needHelpEnabled, setNeedHelpEnabled] = useState(false);

  useEffect(() => {
    if (!operatorId && operators.length > 0) {
      setOperatorId(operators[0].operator_id);
    }
  }, [operatorId, operators]);

  const settings = operators.find(
    (operator) => operator.operator_id === operatorId
  );

  useEffect(() => {
    if (settings) {
      setGracePeriodMinutes(settings.grace_period_minutes);
      setReminderIntervalMinutes(settings.reminder_interval_minutes);
      setNeedHelpEnabled(settings.need_help_enabled);
    }
  }, [settings]);

  const [updateSettings, { isLoading: isSaving }] =
    useUpdateOperatorWelfareCheckSettingsMutation();

  const [triggerPoll, { isLoading: isTriggering, data: pollResult }] =
    useTriggerWelfareCheckPollMutation();

  const handleSave = () => {
    updateSettings({
      operatorId,
      grace_period_minutes: Number(gracePeriodMinutes),
      reminder_interval_minutes: Number(reminderIntervalMinutes),
      need_help_enabled: needHelpEnabled,
    });
  };

  if (isLoading) return <div className="ts-page">Loading...</div>;

  return (
    <div className="ts-page">
      <Typography variant="h2" gutterBottom>
        Welfare Check Settings
      </Typography>

      <Typography variant="h4" className="py-2">
        Run now
      </Typography>
      <TSButton
        btnText="Run Welfare Check now"
        onClickFunc={() => triggerPoll()}
        isLoading={isTriggering}
      />
      {pollResult && (
        <Typography variant="body2" className="py-2">
          Welfare Check started. Any due checks are handled within a minute.
        </Typography>
      )}

      <Typography variant="h4" className="py-2" style={{ marginTop: '1.5rem' }}>
        Operator defaults
      </Typography>
      {operators.length === 0 ? (
        <Typography variant="body1">
          You aren't an Admin for any Operator.
        </Typography>
      ) : (
        <div className="flex flex-col gap-4" style={{ maxWidth: '320px' }}>
          {operators.length > 1 ? (
            <TextField
              select
              label="Operator"
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
            >
              {operators.map((operator) => (
                <MenuItem key={operator.operator_id} value={operator.operator_id}>
                  {operator.operator_name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body1">{settings?.operator_name}</Typography>
          )}
          <TextField
            type="number"
            label="Grace Period (minutes)"
            value={gracePeriodMinutes}
            onChange={(e) => setGracePeriodMinutes(e.target.value)}
          />
          <TextField
            type="number"
            label="Reminder Interval (minutes)"
            value={reminderIntervalMinutes}
            onChange={(e) => setReminderIntervalMinutes(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={needHelpEnabled}
                onChange={(e) => setNeedHelpEnabled(e.target.checked)}
              />
            }
            label={'"I need help" enabled'}
          />
          <TSButton
            btnText="Save"
            onClickFunc={handleSave}
            isLoading={isSaving}
          />
        </div>
      )}
    </div>
  );
};

export default WelfareCheckSettings;
